/**
 * StatusBadge Component
 * 
 * Displays status with consistent styling across the application.
 * Supports common statuses like enabled/disabled, active/expired, etc.
 */

import React from 'react';

export type StatusType = 
  | 'enabled' 
  | 'disabled' 
  | 'active' 
  | 'expired' 
  | 'pending' 
  | 'error' 
  | 'revoked' 
  | 'invalid'
  | 'healthy'
  | 'unhealthy'
  | 'warning';

export interface StatusBadgeProps {
  /** Status type */
  status: StatusType;
  /** Override default label */
  label?: string;
  /** Size variant */
  size?: 'sm' | 'md';
}

const statusConfig: Record<StatusType, { label: string; classes: string }> = {
  enabled: {
    label: 'Enabled',
    classes: 'bg-green-100 text-green-800 border-green-200',
  },
  disabled: {
    label: 'Disabled',
    classes: 'bg-gray-100 text-gray-800 border-gray-200',
  },
  active: {
    label: 'Active',
    classes: 'bg-green-100 text-green-800 border-green-200',
  },
  expired: {
    label: 'Expired',
    classes: 'bg-red-100 text-red-800 border-red-200',
  },
  pending: {
    label: 'Pending',
    classes: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  error: {
    label: 'Error',
    classes: 'bg-red-100 text-red-800 border-red-200',
  },
  revoked: {
    label: 'Revoked',
    classes: 'bg-gray-100 text-gray-800 border-gray-200',
  },
  invalid: {
    label: 'Invalid',
    classes: 'bg-red-100 text-red-800 border-red-200',
  },
  healthy: {
    label: 'Healthy',
    classes: 'bg-green-100 text-green-800 border-green-200',
  },
  unhealthy: {
    label: 'Unhealthy',
    classes: 'bg-red-100 text-red-800 border-red-200',
  },
  warning: {
    label: 'Warning',
    classes: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
};

const sizeClasses = {
  sm: 'px-1.5 py-0.5 text-xs',
  md: 'px-2 py-1 text-xs',
};

export function StatusBadge({ status, label, size = 'md' }: StatusBadgeProps) {
  const config = statusConfig[status];
  
  return (
    <span 
      className={`${sizeClasses[size]} font-medium rounded-full border ${config.classes}`}
    >
      {label || config.label}
    </span>
  );
}

/**
 * Helper to convert boolean enabled state to StatusType
 */
// Small co-located helper; not worth a separate module just for fast-refresh.
// eslint-disable-next-line react-refresh/only-export-components
export function enabledToStatus(enabled: boolean): StatusType {
  return enabled ? 'enabled' : 'disabled';
}
